import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  type LucideIcon,
  X,
  XCircle,
} from "lucide-react";

import { cn } from "@/lib/utils";

/* Toast — guide §5: top-right stack, white card with a 4px left border in
   the tone color, 5s auto-dismiss for success/info, manual for errors.
   Subscribe to mutation lifecycle with `useToast()` from anywhere. */

export type ToastTone = "success" | "info" | "warning" | "error";

export interface ToastOptions {
  title?: string;
  description?: ReactNode;
  tone?: ToastTone;
  /** Override the default duration. Pass 0 to disable auto-dismiss. */
  durationMs?: number;
}

interface ToastEntry extends Required<Omit<ToastOptions, "durationMs">> {
  id: number;
  durationMs: number;
}

interface ToastContextValue {
  push: (opts: ToastOptions) => number;
  dismiss: (id: number) => void;
  success: (msg: string, description?: ReactNode) => number;
  error: (msg: string, description?: ReactNode) => number;
  info: (msg: string, description?: ReactNode) => number;
  warning: (msg: string, description?: ReactNode) => number;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const DEFAULT_DURATION: Record<ToastTone, number> = {
  success: 5000,
  info: 5000,
  warning: 7000,
  error: 0, // require manual dismiss for errors per §5
};

const TONE_BORDER: Record<ToastTone, string> = {
  success: "border-l-[#10B981]",
  info: "border-l-[#06B6D4]",
  warning: "border-l-[#F59E0B]",
  error: "border-l-[#EF4444]",
};

const TONE_ICON: Record<ToastTone, LucideIcon> = {
  success: CheckCircle2,
  info: Info,
  warning: AlertTriangle,
  error: XCircle,
};

const TONE_ICON_COLOR: Record<ToastTone, string> = {
  success: "text-[#10B981]",
  info: "text-[#06B6D4]",
  warning: "text-[#F59E0B]",
  error: "text-[#EF4444]",
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  // Mirror of `toasts` so push() can dedupe synchronously — several widgets
  // failing in the same render commit must still collapse to one toast.
  const toastsRef = useRef<ToastEntry[]>([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id: number) => {
    toastsRef.current = toastsRef.current.filter((t) => t.id !== id);
    setToasts(toastsRef.current);
  }, []);

  const push = useCallback((opts: ToastOptions) => {
    const tone = opts.tone ?? "info";
    const title = opts.title ?? "";
    const description = opts.description ?? "";
    // Suppress an exact duplicate that's still on screen (only comparable
    // when the description is plain text, not an arbitrary ReactNode).
    if (typeof description === "string") {
      const existing = toastsRef.current.find(
        (t) =>
          t.tone === tone && t.title === title && t.description === description,
      );
      if (existing) return existing.id;
    }
    const id = ++idRef.current;
    const entry: ToastEntry = {
      id,
      tone,
      title,
      description,
      durationMs: opts.durationMs ?? DEFAULT_DURATION[tone],
    };
    toastsRef.current = [...toastsRef.current, entry];
    setToasts(toastsRef.current);
    return id;
  }, []);

  const value = useMemo<ToastContextValue>(
    () => ({
      push,
      dismiss,
      success: (msg, description) =>
        push({ title: msg, description, tone: "success" }),
      info: (msg, description) =>
        push({ title: msg, description, tone: "info" }),
      warning: (msg, description) =>
        push({ title: msg, description, tone: "warning" }),
      error: (msg, description) =>
        push({ title: msg, description, tone: "error" }),
    }),
    [push, dismiss],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toaster toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used inside <ToastProvider>");
  }
  return ctx;
}

interface ToasterProps {
  toasts: ToastEntry[];
  onDismiss: (id: number) => void;
}

function Toaster({ toasts, onDismiss }: ToasterProps) {
  return (
    <div
      aria-live="polite"
      aria-label="Notifications"
      className="pointer-events-none fixed right-4 top-4 z-[1100] flex w-full max-w-sm flex-col gap-2"
    >
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

function ToastItem({
  toast,
  onDismiss,
}: {
  toast: ToastEntry;
  onDismiss: (id: number) => void;
}) {
  const { id, tone, title, description, durationMs } = toast;
  const Icon = TONE_ICON[tone];

  useEffect(() => {
    if (durationMs <= 0) return;
    const timer = window.setTimeout(() => onDismiss(id), durationMs);
    return () => window.clearTimeout(timer);
  }, [id, durationMs, onDismiss]);

  return (
    <div
      role={tone === "error" ? "alert" : "status"}
      className={cn(
        "pointer-events-auto flex items-start gap-3 rounded-btn border-l-4 bg-white px-4 py-3 shadow-toast",
        "animate-in fade-in slide-in-from-right-2",
        TONE_BORDER[tone],
      )}
    >
      <Icon
        size={18}
        strokeWidth={1.5}
        aria-hidden="true"
        className={cn("mt-0.5 shrink-0", TONE_ICON_COLOR[tone])}
      />
      <div className="min-w-0 flex-1">
        {title && (
          <p className="text-sm font-semibold text-navy">{title}</p>
        )}
        {description && (
          <p className="mt-0.5 text-xs text-slate">{description}</p>
        )}
      </div>
      <button
        type="button"
        onClick={() => onDismiss(id)}
        aria-label="Dismiss"
        className="shrink-0 rounded text-slate hover:text-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-blue/30"
      >
        <X size={14} strokeWidth={1.5} aria-hidden="true" />
      </button>
    </div>
  );
}
